'use client';

import Image from 'next/image';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { useInertBackground } from '@/hooks/useInertBackground';

/**
 * Fullscreen YouTube dialog shared by the reel triggers (about bar,
 * why-choose teaser). Rendered via portal to <body> because ancestors carry
 * transforms (framer-motion) and overflow-hidden, which would trap and clip
 * a fixed overlay. Escape, the close button, or a backdrop click dismisses.
 */
export function ReelVideoDialog({
  src,
  mobileSrc,
  poster,
  ariaLabel,
  onClose,
}: {
  src: string;
  /** Lighter encode for small screens; picked once, when the dialog opens. */
  mobileSrc?: string;
  poster?: string;
  ariaLabel: string;
  onClose: () => void;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  // Page behind the dialog is inert: Tab cannot leak out of it (WCAG 2.4.3).
  // The hook also returns focus to the opener on close.
  useInertBackground(dialogRef);
  const [videoSrc] = useState(() =>
    mobileSrc && window.matchMedia('(max-width: 767px)').matches ? mobileSrc : src,
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    // Return focus to whatever opened the dialog (the reel card), so a
    // keyboard user is not dropped at the top of the page on close.
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    // Capture phase, so Escape still closes the dialog while keyboard focus
    // is on the native <video> (checked in Chromium and WebKit, 2026-10-16).
    // In native fullscreen the browser takes the first Escape to leave
    // fullscreen; a second Escape closes the dialog.
    document.addEventListener('keydown', onKey, true);
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.body.style.overflow = '';
      opener?.focus({ preventScroll: true });
    };
  }, [onClose]);

  return createPortal(
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 px-3 pb-3 pt-16 md:px-6 md:pb-6"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <button
        ref={closeRef}
        type="button"
        onClick={onClose}
        aria-label="Close video"
        className="absolute right-3 top-2.5 flex size-11 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent md:right-6"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="size-5"
          aria-hidden="true"
        >
          <path d="M6 6l12 12M18 6L6 18" />
        </svg>
      </button>
      {/* As large as the viewport allows: full width, unless 16:9 at that
          width would be taller than the space left under the close button
          (4rem top + 1.5rem bottom), in which case height sets the size. */}
      <div className="aspect-video w-[min(100%,calc((100svh-5.5rem)*16/9))]">
        {/* Self-hosted — loads only when the dialog opens, so it costs
            nothing on page load. */}
        {/* eslint-disable-next-line jsx-a11y/media-has-caption -- the reel has burned-in subtitles */}
        <video
          src={videoSrc}
          poster={poster}
          controls
          autoPlay
          playsInline
          className="size-full rounded-lg bg-black"
        />
      </div>
    </div>,
    document.body,
  );
}

interface ReelLightboxProps {
  /** Full self-hosted reel, played in the dialog. */
  videoSrc: string;
  /** Lighter encode for phones. */
  mobileVideoSrc?: string;
  /** Local poster frame shown on the trigger thumbnail. */
  posterSrc: string;
  /** Visible label next to the thumbnail, e.g. "Play reel" */
  label: string;
  /** Accessible name for the play button */
  ariaLabel: string;
}

/**
 * Reel bar trigger + fullscreen YouTube lightbox. The trigger shows the
 * video's poster frame with a play glyph; clicking opens the shared dialog.
 */
export function ReelLightbox({
  videoSrc,
  mobileVideoSrc,
  posterSrc,
  label,
  ariaLabel,
}: ReelLightboxProps) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        data-analytics-cta="about_play_reel"
        data-analytics-location="home_about"
        className="group flex items-center gap-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 md:gap-8"
        aria-label={ariaLabel}
      >
        <span className="relative block h-[56px] w-[100px] shrink-0 overflow-hidden rounded-md md:h-[80px] md:w-[142px] lg:h-[110px] lg:w-[196px]">
          <Image
            src={posterSrc}
            alt=""
            fill
            sizes="(max-width: 768px) 100px, (max-width: 1024px) 142px, 196px"
            className="object-cover transition-transform duration-300 group-hover:scale-105"
          />
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="flex size-8 items-center justify-center rounded-full bg-accent transition-transform duration-300 group-hover:scale-110 md:size-10 lg:size-12">
              <svg
                viewBox="0 0 24 24"
                fill="currentColor"
                className="ml-0.5 size-3.5 text-background md:size-4 lg:size-5"
                aria-hidden="true"
              >
                <path d="M8 5v14l11-7z" />
              </svg>
            </span>
          </span>
        </span>
        <span className="font-light text-[22px] leading-none tracking-[-0.02em] text-[#121212] lg:text-[40px]">
          {label}
        </span>
      </button>

      {open && (
        <ReelVideoDialog
          src={videoSrc}
          mobileSrc={mobileVideoSrc}
          poster={posterSrc}
          ariaLabel={ariaLabel}
          onClose={close}
        />
      )}
    </>
  );
}
